import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Card, Stack, Link, Container, Typography, Divider } from '@mui/material';
// routes
import { PATH_AUTH } from '../../routes/paths';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import Image from '../../components/Image';
// sections
import { Auth2FAForm, AuthValidate2FACodeForm, SsoSignInButton } from '../../sections/auth/login';
// assets
import { LoginGraphic } from '../../assets';
// util
import { BUILD_TYPE } from '../../config';
import axiosInstance from '../../utils/axios';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

const NativAppStyle = {
  paddingTop: 'env(safe-area-inset-top)',
};

// ----------------------------------------------------------------------

type RegisterValues = {
  email: string;
  password: string;
};

const initRegisterValues: RegisterValues = {
  email: '',
  password: '',
};

// ----------------------------------------------------------------------

export default function Login() {
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');
  const buildType: string = process.env.REACT_APP_BUILD_TYPE || 'build-type-not-set';
  const [registerState, setRegisterState] = useState(initRegisterValues);
  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(null);
  // REMOVE WITH KEYCLOAK_SSO_ENABLED — cleanup epic
  const [linkedUserInterstitial, setLinkedUserInterstitial] = useState(false);

  useEffect(() => {
    const fetchAuthConfig = async () => {
      try {
        const response = await axiosInstance.get('/config/auth');
        setSsoEnabled(response.data?.sso_enabled === true);
      } catch {
        setSsoEnabled(false);
      }
    };

    fetchAuthConfig();
  }, []);

  const onValidateSubmitted = (values: RegisterValues) => {
    setRegisterState(values);
  };

  const onCancelValidateCode = () => {
    setRegisterState(initRegisterValues);
  };

  return (
    <Page title="Login">
      <RootStyle>
        <HeaderStyle>
          <Logo sx={{ ...NativAppStyle }} />
          {smUp && buildType === BUILD_TYPE.WEB && (
            <Typography variant="body2" sx={{ mt: { md: -2 } }}>
              Don’t have an account? {''}
              <Link variant="subtitle2" component={RouterLink as any} to={PATH_AUTH.register}>
                Get started
              </Link>
            </Typography>
          )}
        </HeaderStyle>

        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              A Few More Clicks To Login To Your Account
            </Typography>
            <Image sx={{ px: 5, overflow: 'initial' }} src={LoginGraphic} alt="login" />
          </SectionStyle>
        )}

        <Container maxWidth="sm">
          <ContentStyle>
            {/* REMOVE WITH KEYCLOAK_SSO_ENABLED — cleanup epic: hide header + SSO button when interstitial is active */}
            {!linkedUserInterstitial && (
              <Stack direction="row" alignItems="center" sx={{ mb: 5 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    Sign in to Bizhaven
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    Only a few clicks and you'll be on your way!
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* old login */}
            {/* <LoginForm /> */}

            {/* REMOVE WITH KEYCLOAK_SSO_ENABLED — cleanup epic */}
            {!linkedUserInterstitial && (!registerState.email || !registerState.password) && ssoEnabled === true && (
              <>
                <SsoSignInButton />
                <Divider
                  sx={{
                    my: 2,
                    typography: 'overline',
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                  }}
                >
                  OR SIGN IN BELOW
                </Divider>
              </>
            )}

            {(!registerState.email || !registerState.password) && (
              <Auth2FAForm onSuccess={onValidateSubmitted} onSsoRequired={() => setLinkedUserInterstitial(true)} />
            )}

            {(registerState.email || registerState.password) && (
              <AuthValidate2FACodeForm
                email={registerState.email}
                password={registerState.password}
                onCancel={onCancelValidateCode}
              />
            )}

            {!smUp && buildType === BUILD_TYPE.WEB && (
              <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                Don’t have an account?{' '}
                <Link variant="subtitle2" component={RouterLink as any} to={PATH_AUTH.register}>
                  Get started
                </Link>
              </Typography>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
